import { useState, useEffect, useCallback } from 'react';
import { 
  paymentHistoryService, 
  PaymentHistoryResponse, 
  PaymentHistoryFilters,
  PaymentHistoryItem 
} from '../services/paymentHistoryService';

export interface UsePaymentHistoryReturn {
  // Data
  paymentHistory: PaymentHistoryItem[];
  summary: {
    totalPayments: number;
    totalAmount: number;
    statusCounts: {
      completed: number;
      pending: number;
      failed: number;
    };
  } | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    limit: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  } | null;
  
  // Loading and error states
  loading: boolean;
  error: string | null;
  
  // Actions
  fetchPaymentHistory: (filters?: PaymentHistoryFilters) => Promise<void>;
  refreshPaymentHistory: () => Promise<void>;
  
  // Filter state
  filters: PaymentHistoryFilters;
  setFilters: (filters: PaymentHistoryFilters) => void;
  clearFilters: () => void;
}

export const usePaymentHistory = (initialFilters: PaymentHistoryFilters = {}): UsePaymentHistoryReturn => {
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistoryItem[]>([]);
  const [summary, setSummary] = useState<UsePaymentHistoryReturn['summary']>(null);
  const [pagination, setPagination] = useState<UsePaymentHistoryReturn['pagination']>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFiltersState] = useState<PaymentHistoryFilters>({
    page: 1,
    limit: 10,
    ...initialFilters
  });

  const fetchPaymentHistory = useCallback(async (filtersToUse?: PaymentHistoryFilters) => {
    const filtersToApply = filtersToUse || filters;
    setLoading(true);
    setError(null);
    
    try {
      const response: PaymentHistoryResponse = await paymentHistoryService.getPaymentHistory(filtersToApply);
      
      if (response.success) {
        setPaymentHistory(response.data.paymentHistory);
        setSummary(response.data.summary);
        setPagination(response.data.pagination);
      } else {
        throw new Error('Failed to fetch payment history');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      console.error('❌ Error fetching payment history:', errorMessage);
      setError(errorMessage);
      setPaymentHistory([]);
      setSummary(null);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshPaymentHistory = useCallback(async () => {
    setFiltersState(currentFilters => {
      fetchPaymentHistory(currentFilters);
      return currentFilters;
    });
  }, [fetchPaymentHistory]);

  const setFilters = useCallback((newFilters: PaymentHistoryFilters) => {
    setFiltersState(prevFilters => {
      const updatedFilters = { ...prevFilters, ...newFilters };
      // Fetch with updated filters
      fetchPaymentHistory(updatedFilters);
      return updatedFilters;
    });
  }, [fetchPaymentHistory]);

  const clearFilters = useCallback(() => {
    const clearedFilters: PaymentHistoryFilters = {
      page: 1,
      limit: 10
    };
    setFiltersState(clearedFilters);
    fetchPaymentHistory(clearedFilters);
  }, [fetchPaymentHistory]);

  // Initial fetch on mount only
  useEffect(() => {
    fetchPaymentHistory(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  return {
    paymentHistory,
    summary,
    pagination,
    loading,
    error,
    fetchPaymentHistory,
    refreshPaymentHistory,
    filters,
    setFilters,
    clearFilters
  };
};
